import { useEffect, useState } from 'react';
import { usePdfOrderUpload } from '../lib/pdf-order-upload.ts';
import { cn } from '../lib/utils.ts';
import { PdfUploadStatus } from './pdf-upload-status.tsx';

/**
 * PDF-Bestellung auf jede Seite ziehen (EXPERIENCE §2.4, Story 9.1): Eine Datei, die keine Fläche der Seite selbst
 * annimmt (z. B. der Chatter mit „Datei anhängen“), wird als PDF-Bestellung hochgeladen; daraus entsteht wie über
 * „+ Neu › Auftrag aus PDF“ ein Auftragsentwurf. Während des Ziehens ein ruhiger Hinweis, danach der Stand unten mittig
 * (am Handy über die volle Breite minus Rand). Story 9.14: auch Foto und Excel; andere Dateien: ein Satz, nichts angelegt.
 */
export function PdfDropHost() {
  const { state, upload, reset } = usePdfOrderUpload();
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    const hasFiles = (e: globalThis.DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
    let leaveTimer: number | undefined;
    const onOver = (e: globalThis.DragEvent) => {
      if (!hasFiles(e)) return;
      window.clearTimeout(leaveTimer);
      // Eine Fläche der Seite nimmt die Datei selbst an (z. B. Chatter): kein Hinweis auf einen Entwurf.
      if (e.defaultPrevented) {
        setDragging(false);
        return;
      }
      // Ohne preventDefault öffnet der Browser die Datei selbst (und verlässt das ERP).
      e.preventDefault();
      setDragging(true);
    };
    const onLeave = () => {
      window.clearTimeout(leaveTimer);
      leaveTimer = window.setTimeout(() => setDragging(false), 120);
    };
    const onDrop = (e: globalThis.DragEvent) => {
      setDragging(false);
      // Schon von einer Fläche der Seite angenommen (Chatter, Ablage auf der Entwurfsseite): nichts weiter.
      if (e.defaultPrevented || !hasFiles(e)) return;
      e.preventDefault();
      void upload(e.dataTransfer?.files[0]);
    };
    window.addEventListener('dragover', onOver);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.clearTimeout(leaveTimer);
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [upload]);

  // Erfolg und Fehler bleiben 12 s stehen (Bedien-Konstante, keine fachliche Frist); „liest noch“ bleibt bis zum Ergebnis.
  const finished = state.kind === 'created' || state.kind === 'error' ? state.kind : null;
  useEffect(() => {
    if (!finished) return;
    const timer = window.setTimeout(reset, 12_000);
    return () => window.clearTimeout(timer);
  }, [finished, reset]);

  if (!dragging && state.kind === 'idle') return null;
  return (
    <div
      aria-live="polite"
      className={cn(
        'pointer-events-none fixed inset-x-4 bottom-6 z-40 flex justify-center',
        'min-[640px]:inset-x-auto min-[640px]:left-1/2 min-[640px]:-translate-x-1/2',
      )}
      data-testid="pdf-ablage-seite"
    >
      <div className="pointer-events-auto w-full max-w-[560px] rounded-tile bg-card shadow-float">
        {dragging ? <p className="type-meta px-4 py-3 text-ink-2">Bestellung (PDF, Foto oder Excel) hier ablegen – daraus wird ein Auftragsentwurf.</p> : <PdfUploadStatus state={state} />}
      </div>
    </div>
  );
}
